'use es6';

import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import PortalIdParser from 'PortalIdParser';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import UILink from 'UIComponents/link/UILink';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { FILE_MANAGER_DELETE_SCOPE, ObjectCategory, RequestStatus } from 'FileManagerCore/Constants';
import requestStatusProp from 'FileManagerCore/types/props/RequestStatusProp';
import { getIsReadOnly } from 'FileManagerCore/selectors/Permissions';
import ScopedFeatureTooltip from 'FileManagerCore/components/permissions/ScopedFeatureTooltip';
import { closeModal } from '../../actions/Actions';
import { bulkDelete, getNestedFolderFileCount } from '../../actions/Bulk';
import { getSelectedFilesAndFolders, getTotalFileCount, getTotalNestedFileCountRequestStatus } from '../../selectors/Selection';
import { selectIsUngatedForPermissions, selectUserHasFileManagerDeleteScope } from 'FileManagerCore/selectors/Auth';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function i18nKey(suffix) {
  return `FileManagerDashboard.bulkTrash.${suffix}`;
}
class BulkDeleteButton extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      isOpen: false
    };
    this.handleConfirmDelete = () => {
      const {
        onBulkDelete,
        bulkSelection
      } = this.props;
      this.setState({
        isOpen: !this.state.isOpen
      });
      this.props.closeModal();
      onBulkDelete(bulkSelection);
    };
    this.handleCancel = () => {
      this.setState({
        isOpen: false
      });
      this.props.closeModal();
    };
    this.handleDeleteClick = event => {
      event.preventDefault();
      event.stopPropagation();
      this.props.getNestedFolderFileCount(this.getSelectedFolderIds(), false);
      this.setState({
        isOpen: true
      });
    };
  }
  getSelectedFolderIds() {
    return this.props.bulkSelection.filter(object => object.get('category') === ObjectCategory.FOLDER).keySeq().toList();
  }
  isLoadingFileCount() {
    return [RequestStatus.PENDING, RequestStatus.UNINITIALIZED].includes(this.props.totalNestedFileCountRequestStatus);
  }
  renderMessage() {
    const {
      totalFileCount
    } = this.props;
    if (this.isLoadingFileCount()) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered"
      });
    }
    const folderCount = this.getSelectedFolderIds().size;
    if (folderCount) {
      if (totalFileCount) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('messageWithFilesAndFolders'),
          options: {
            count: folderCount,
            fileCount: totalFileCount
          }
        });
      }
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('messageWithEmptyFolders'),
        options: {
          count: folderCount
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('message'),
      options: {
        count: totalFileCount
      }
    });
  }
  renderConfirmModal(totalNestedFileCountRequestStatus) {
    return /*#__PURE__*/_jsx(UIConfirmModal, {
      "data-test-id": "bulk-delete-modal",
      confirmLabel: I18n.text(i18nKey('confirmButton')),
      use: "danger",
      confirmUse: "danger",
      rejectUse: "tertiary-light",
      size: "auto",
      message: this.renderMessage(),
      description: this.isLoadingFileCount() ? /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered",
        size: "small"
      }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('body_jsx'),
        elements: {
          UILink
        },
        options: {
          filesTrashLink: `/files-trash/${PortalIdParser.get()}?sourceUrl=${encodeURIComponent(window.location.toString())}`
        }
      }),
      onConfirm: this.handleConfirmDelete,
      disableConfirm: totalNestedFileCountRequestStatus === RequestStatus.UNINITIALIZED || totalNestedFileCountRequestStatus === RequestStatus.PENDING,
      onReject: this.handleCancel
    });
  }
  render() {
    const {
      isReadOnly,
      isUngatedForPermissions,
      hasDeleteScope,
      totalNestedFileCountRequestStatus
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ScopedFeatureTooltip, {
        scope: FILE_MANAGER_DELETE_SCOPE,
        children: /*#__PURE__*/_jsxs(UILink, {
          onClick: this.handleDeleteClick,
          disabled: isReadOnly || isUngatedForPermissions && !hasDeleteScope,
          "data-test-id": "dashboard-bulk-delete",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "delete"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'FileManagerDashboard.actions.trash'
          })]
        })
      }), this.state.isOpen && this.renderConfirmModal(totalNestedFileCountRequestStatus)]
    });
  }
}
BulkDeleteButton.propTypes = {
  bulkSelection: PropTypes.instanceOf(Immutable.Map).isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  totalFileCount: PropTypes.number,
  totalNestedFileCountRequestStatus: requestStatusProp,
  onBulkDelete: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  getNestedFolderFileCount: PropTypes.func.isRequired,
  isUngatedForPermissions: PropTypes.bool.isRequired,
  hasDeleteScope: PropTypes.bool.isRequired
};
BulkDeleteButton.defaultProps = {
  use: 'tertiary-light'
};
const mapStateToProps = state => ({
  bulkSelection: getSelectedFilesAndFolders(state),
  isReadOnly: getIsReadOnly(state),
  totalFileCount: getTotalFileCount(state),
  totalNestedFileCountRequestStatus: getTotalNestedFileCountRequestStatus(state),
  isUngatedForPermissions: selectIsUngatedForPermissions(state),
  hasDeleteScope: selectUserHasFileManagerDeleteScope(state)
});
const mapDispatchToProps = {
  onBulkDelete: bulkDelete,
  getNestedFolderFileCount,
  closeModal
};
export default connect(mapStateToProps, mapDispatchToProps)(BulkDeleteButton);